import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { ColDef, ColumnApi, GridApi, GridOptions } from 'ag-grid-enterprise';
import * as moment from 'moment';
import { MessageService } from 'primeng/api';
import { ReportService } from '../..';
import 'ag-grid-enterprise';
import { ImportExportComponent } from '@app/_components/_helpers/import-export/import-export.component';

@Component({
    selector: 'app-reservation-summary-report',
    templateUrl: './reservation-summary-report.component.html',
    styleUrls: ['./reservation-summary-report.component.css'],
    standalone: false
})
export class ReservationSummaryReportComponent implements OnInit, AfterViewInit {

	@Input() showSearchUI = true;
	@ViewChild(ImportExportComponent) importExportComponent: ImportExportComponent;
	
	constructor(
		public messageService: MessageService,
		public reportService: ReportService) 
		{ 
			this.defaultColDef = {
				sortable: true,
				filter: true,
				resizable: true,
			};

			this.gridOptions = {
				animateRows: true,
				enableRangeSelection: true,
				pagination: true,
				paginationPageSize: 2500,
				alwaysShowHorizontalScroll: true,
				tooltipShowDelay: 0,
				popupParent: document.body  
			};
		}

	// forms
	formLoading = false;
	rowData: any[] = [];
	columnDefs: ColDef[] = [];
	gridOptions: GridOptions;
	defaultColDef: ColDef;
	defaultStartDate: Date;
	defaultEndDate: Date;
	lastSearchForm: any;

	ngOnInit(): void {
		// Set default dates to current month
		const today = new Date();
		this.defaultStartDate = new Date(today.getFullYear(), today.getMonth(), 1);
		this.defaultEndDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
	}

	loadReport(searchForm) {
		try {
			this.formLoading = true;
			this.lastSearchForm = searchForm; // Store for Excel download
			
			// Format dates as ISO 8601 (YYYY-MM-DD)
			const startDate = moment(searchForm.startDate).format('YYYY-MM-DD');
			const endDate = moment(searchForm.endDate).format('YYYY-MM-DD');

			this.reportService.getReservationSummaryReport(startDate, endDate, 'json').subscribe(
				(res: any) => {
					try {
						if (res && res.data && res.data.reportData) {
							this.rowData = res.data.reportData;
							this.buildColumnDefs();
							
							// Auto-size columns after data load
							setTimeout(() => {
								if (this.gridOptions.api && this.gridOptions.columnApi) {
									const allColumnIds: string[] = [];
									(this.gridOptions as any).columnApi.getAllColumns().forEach((col) => {
										allColumnIds.push(col.getId());
									});
									(this.gridOptions as any).columnApi.autoSizeColumns(allColumnIds, false);
								}
							}, 0);

							this.messageService.add({
								severity: 'success',
								summary: 'Success',
								detail: res.message || 'Reservation summary report retrieved successfully',
								life: 3000
							});
						} else {
							this.rowData = [];
							this.messageService.add({
								severity: 'warn',
								summary: 'No Data',
								detail: 'No data found for the selected date range',
								life: 3000
							});
						}
					} catch (error) {
						console.error('Error processing report data:', error);
						this.messageService.add({
							severity: 'error',
							summary: 'Error',
							detail: 'Failed to process report data',
						});
					} finally {
						this.formLoading = false;
					}
				},
				(err) => {
					console.error('API call failed:', err);
					this.formLoading = false;
					this.messageService.add({
						severity: 'error',
						summary: 'Error',
						detail: err.error?.message || 'Failed to retrieve reservation summary report',
					});
				}
			);
		} catch (error) {
			console.error('Error loading report:', error);
			this.formLoading = false;
			this.messageService.add({
				severity: 'error',
				summary: 'Error',
				detail: 'Failed to load report',
			});
		}
	}

	buildColumnDefs() {
		if (this.rowData.length === 0) {
			this.columnDefs = [];
			return;
		}

		// Get all unique keys from the data
		const allKeys = new Set<string>();
		this.rowData.forEach(row => {
			Object.keys(row).forEach(key => allKeys.add(key));
		});

		this.columnDefs = Array.from(allKeys).map((field: string) => {
			const def: any = {
				headerName: this.prettyHeader(field),
				field: field,
			};

			// Format numeric fields
			if (field === 'actualPrice' || field === 'finalAmount') {
				def.valueFormatter = (params) => this.formatCurrency(params.value);
				def.type = 'numericColumn';
			}

			// Format date fields
			if (field === 'checkInDateTime' || field === 'checkOutDateTime') {
				def.valueFormatter = (params) => this.formatDate(params.value);
			}

			// Format integer fields
			if (field === 'reservationId' || field === 'orderProductId' || field === 'orderId' || 
			    field === 'actualNights' || field === 'reportNights' || field === 'referralSourceId') {
				def.type = 'numericColumn';
			}

			return def;
		});
	}

	downloadExcel() {
		if (!this.lastSearchForm || !this.lastSearchForm.startDate || !this.lastSearchForm.endDate) {
			this.messageService.add({
				severity: 'warn',
				summary: 'Warning',
				detail: 'Please run a search first to download Excel',
				life: 3000
			});
			return;
		}

		try {
			this.formLoading = true;
			
			// Format dates as ISO 8601 (YYYY-MM-DD)
			const startDate = moment(this.lastSearchForm.startDate).format('YYYY-MM-DD');
			const endDate = moment(this.lastSearchForm.endDate).format('YYYY-MM-DD');

			this.reportService.getReservationSummaryReport(startDate, endDate, 'excel').subscribe(
				(blob: Blob) => {
					const downloadUrl = window.URL.createObjectURL(blob);
					const link = document.createElement('a');
					link.href = downloadUrl;
					link.download = `ReservationSummaryReport_${startDate}_to_${endDate}.xlsx`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
					window.URL.revokeObjectURL(downloadUrl);
					
					this.messageService.add({
						severity: 'success',
						summary: 'Success',
						detail: 'Excel file downloaded successfully',
						life: 3000
					});
					this.formLoading = false;
				},
				(err) => {
					console.error('Excel download failed:', err);
					this.formLoading = false;
					this.messageService.add({
						severity: 'error',
						summary: 'Error',
						detail: err.error?.message || 'Failed to download Excel file',
					});
				}
			);
		} catch (error) {
			console.error('Error downloading Excel:', error);
			this.formLoading = false;
			this.messageService.add({
				severity: 'error',
				summary: 'Error',
				detail: 'Failed to download Excel file',
			});
		}
	}

	exportSelected(exporttype: any) {
		this.importExportComponent.exporData(exporttype, this.rowData, 'Reservation Summary Report');
	}

	// Format header names
	prettyHeader(field: string): string {
		return field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
	}

	formatCurrency(value: any): string {
		if (value == null || value === undefined) return '';
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 2,
			maximumFractionDigits: 2
		}).format(value);
	}

	formatDate(value: any): string {
		if (!value) return '';
		return moment(value).format('YYYY-MM-DD HH:mm:ss');
	}

	onGridReady(params: any) {
		(this.gridOptions as any).api = params.api;
		(this.gridOptions as any).columnApi = params.columnApi;
	}

	ngAfterViewInit(): void {
	}

}

